// src/pages/Users/Lexique/utils/lexiqueUtils.js

export const LEXIQUE_UI_STORAGE_KEY = "lexique_ui_state";
export const FAV_KEY = "lexique_favorites";

export const DEFAULT_LEXIQUE_UI = {
  q: "",
  letter: "Toutes",
  sort: "jp",
  favOnly: false,
};

export const collatorFR = new Intl.Collator("fr", {
  sensitivity: "base",
});

export function readJsonStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);

    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

export function strip(value = "") {
  if (value === null || value === undefined) return "";

  return String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

export function firstLetter(value = "") {
  const clean = String(value || "").trim();

  if (!clean) return "#";

  return clean.toUpperCase().charAt(0);
}

export function hasJapaneseCharacters(value = "") {
  return /[\u3040-\u30FF\u4E00-\u9FFF]/.test(value);
}

export function pickVoice(langStartsWith = "ja", fallbackLang = "ja-JP") {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    return {
      voice: null,
      lang: fallbackLang,
    };
  }

  const voices = window.speechSynthesis.getVoices?.() || [];

  const voice =
    voices.find((item) =>
      item.lang?.toLowerCase().startsWith(langStartsWith)
    ) || null;

  return {
    voice,
    lang: voice?.lang || fallbackLang,
  };
}

export function speakWith(langStartsWith, text, fallbackLang) {
  try {
    if (typeof window === "undefined") return;
    if (!window.speechSynthesis || !text) return;

    window.speechSynthesis.cancel();

    const { voice, lang } = pickVoice(langStartsWith, fallbackLang);

    const utterance = new SpeechSynthesisUtterance(text);

    if (voice) utterance.voice = voice;

    utterance.lang = lang;
    utterance.rate = langStartsWith === "ja" ? 0.92 : 1;
    utterance.pitch = 1;

    window.speechSynthesis.speak(utterance);
  } catch {
    // Non bloquant.
  }
}

export function buildAvailableLetters(data = []) {
  const safeData = Array.isArray(data) ? data.filter(Boolean) : [];

  const letters = Array.from(
    new Set(
      safeData
        .map((row) => firstLetter(row?.japonais))
        .filter((letter) => letter && letter !== "#")
    )
  ).sort((a, b) => collatorFR.compare(a, b));

  return [DEFAULT_LEXIQUE_UI.letter, ...letters];
}

export function filterAndSortLexique(data = [], ui, favorites = []) {
  const safeData = Array.isArray(data) ? data.filter(Boolean) : [];

  const safeUi = {
    ...DEFAULT_LEXIQUE_UI,
    ...(ui || {}),
  };

  const query = strip(safeUi.q);

  const filtered = safeData.filter((row) => {
    const japonais = row?.japonais || "";
    const traduction = row?.traduction || "";

    const matchesSearch =
      !query || strip(japonais).includes(query) || strip(traduction).includes(query);

    const matchesLetter =
      safeUi.letter === DEFAULT_LEXIQUE_UI.letter ||
      firstLetter(japonais) === safeUi.letter;

    const matchesFavorite = !safeUi.favOnly || favorites.includes(japonais);

    return matchesSearch && matchesLetter && matchesFavorite;
  });

  filtered.sort((a, b) => {
    if (safeUi.sort === "fr") {
      return (
        collatorFR.compare(String(a?.traduction || ""), String(b?.traduction || "")) ||
        collatorFR.compare(String(a?.japonais || ""), String(b?.japonais || ""))
      );
    }

    return (
      collatorFR.compare(String(a?.japonais || ""), String(b?.japonais || "")) ||
      collatorFR.compare(String(a?.traduction || ""), String(b?.traduction || ""))
    );
  });

  return filtered;
}

export function buildLexiqueGroups(filtered = [], selectedLetter = "Toutes") {
  const map = new Map();

  filtered.forEach((row) => {
    const letter =
      selectedLetter === DEFAULT_LEXIQUE_UI.letter
        ? firstLetter(row?.japonais)
        : selectedLetter;

    if (!map.has(letter)) {
      map.set(letter, []);
    }

    map.get(letter).push(row);
  });

  const keys = Array.from(map.keys()).sort((a, b) => collatorFR.compare(a, b));

  return keys.map((letter) => ({
    letter,
    items: map.get(letter),
  }));
}

export function hasActiveLexiqueFilters(ui) {
  const safeUi = {
    ...DEFAULT_LEXIQUE_UI,
    ...(ui || {}),
  };

  return (
    Boolean(safeUi.q) ||
    safeUi.letter !== DEFAULT_LEXIQUE_UI.letter ||
    safeUi.sort !== DEFAULT_LEXIQUE_UI.sort ||
    safeUi.favOnly
  );
}

export function exportLexiqueCSV(rows = [], discipline = "judo") {
  const header = ["japonais", "traduction"];

  const csvRows = [
    header,
    ...rows.map((row) => [row?.japonais || "", row?.traduction || ""]),
  ];

  const csv = csvRows
    .map((row) =>
      row
        .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
        .join(";")
    )
    .join("\n");

  const blob = new Blob([csv], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = `lexique_${discipline || "judo"}.csv`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

export function highlightParts(text, query) {
  const value = String(text ?? "");
  const q = strip(query || "");

  if (!q) {
    return [
      {
        text: value,
        match: false,
      },
    ];
  }

  const normalizedValue = strip(value);
  const index = normalizedValue.indexOf(q);

  if (index === -1) {
    return [
      {
        text: value,
        match: false,
      },
    ];
  }

  return [
    {
      text: value.slice(0, index),
      match: false,
    },
    {
      text: value.slice(index, index + query.length),
      match: true,
    },
    {
      text: value.slice(index + query.length),
      match: false,
    },
  ].filter((part) => part.text);
}